/**
 * Cities Sagas
 * Async operations for cities domain
 */

import { call, put, takeLatest, debounce, all, fork, select } from '@spcc/core'
import type { PayloadAction } from '@spcc/core'

import { citiesActions } from './cities.slice'
import { selectCitiesFilter } from './cities.selectors'
import { citiesApi, regionsForCitiesApi, countriesForCitiesApi } from '../infrastructure/api/cities-api'
import type {
  City,
  CitiesFilter,
  CreateCityRequest,
  UpdateCityRequest,
  PaginatedCities,
  CityStats,
  RegionSummary,
  CountrySummary,
} from '../domain/types'

// ===========================================
// Fetch Cities Saga
// ===========================================

function* fetchCitiesSaga(action: PayloadAction<CitiesFilter | undefined>) {
  try {
    const filter = action.payload ?? (yield select(selectCitiesFilter))
    const result: PaginatedCities = yield call(citiesApi.getCitiesPaginated, filter)
    yield put(
      citiesActions.fetchCitiesSuccess({
        data: result.data,
        pagination: result.pagination,
      })
    )
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Failed to fetch cities'
    yield put(citiesActions.fetchCitiesFailure(message))
  }
}

// ===========================================
// Fetch City Detail Saga
// ===========================================

function* fetchCityDetailSaga(action: PayloadAction<string>) {
  try {
    const city: City | null = yield call(citiesApi.getCity, action.payload)
    if (city) {
      yield put(citiesActions.fetchCityDetailSuccess(city))
    } else {
      yield put(citiesActions.fetchCityDetailFailure('City not found'))
    }
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Failed to fetch city detail'
    yield put(citiesActions.fetchCityDetailFailure(message))
  }
}

// ===========================================
// Fetch City Stats Saga
// ===========================================

function* fetchCityStatsSaga() {
  try {
    const stats: CityStats = yield call(citiesApi.getCityStats)
    yield put(citiesActions.fetchCityStatsSuccess(stats))
  } catch (error) {
    // Stats fetch failure is non-critical, just log
    console.error('Failed to fetch city stats:', error)
  }
}

// ===========================================
// Fetch Available Regions Saga
// ===========================================

function* fetchAvailableRegionsSaga() {
  try {
    const regions: RegionSummary[] = yield call(regionsForCitiesApi.getActiveRegions)
    yield put(citiesActions.fetchAvailableRegionsSuccess(regions))
  } catch (error) {
    console.error('Failed to fetch available regions:', error)
  }
}

// ===========================================
// Fetch Available Countries Saga
// ===========================================

function* fetchAvailableCountriesSaga() {
  try {
    const countries: CountrySummary[] = yield call(countriesForCitiesApi.getActiveCountries)
    yield put(citiesActions.fetchAvailableCountriesSuccess(countries))
  } catch (error) {
    console.error('Failed to fetch available countries:', error)
  }
}

// ===========================================
// Create City Saga
// ===========================================

function* createCitySaga(action: PayloadAction<CreateCityRequest>) {
  try {
    const city: City = yield call(citiesApi.createCity, action.payload)
    yield put(citiesActions.createCitySuccess(city))
    // Refresh stats after creation
    yield put(citiesActions.fetchCityStats())
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Failed to create city'
    yield put(citiesActions.createCityFailure(message))
  }
}

// ===========================================
// Update City Saga
// ===========================================

function* updateCitySaga(
  action: PayloadAction<{ documentId: string; data: UpdateCityRequest }>
) {
  try {
    const city: City = yield call(
      citiesApi.updateCity,
      action.payload.documentId,
      action.payload.data
    )
    yield put(citiesActions.updateCitySuccess(city))
    // Refresh stats after update
    yield put(citiesActions.fetchCityStats())
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Failed to update city'
    yield put(citiesActions.updateCityFailure(message))
  }
}

// ===========================================
// Delete City Saga
// ===========================================

function* deleteCitySaga(action: PayloadAction<string>) {
  try {
    const success: boolean = yield call(citiesApi.deleteCity, action.payload)
    if (success) {
      yield put(citiesActions.deleteCitySuccess(action.payload))
      // Refresh stats after deletion
      yield put(citiesActions.fetchCityStats())
    } else {
      yield put(citiesActions.deleteCityFailure('Failed to delete city'))
    }
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Failed to delete city'
    yield put(citiesActions.deleteCityFailure(message))
  }
}

// ===========================================
// Fetch Cities On Filter Change Saga
// ===========================================

function* fetchCitiesOnFilterChangeSaga() {
  try {
    const filter: CitiesFilter = yield select(selectCitiesFilter)
    const result: PaginatedCities = yield call(citiesApi.getCitiesPaginated, filter)
    yield put(
      citiesActions.fetchCitiesSuccess({
        data: result.data,
        pagination: result.pagination,
      })
    )
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Failed to fetch cities'
    yield put(citiesActions.fetchCitiesFailure(message))
  }
}

// ===========================================
// Watcher
// ===========================================

function* watchCities() {
  yield takeLatest(citiesActions.fetchCities.type, fetchCitiesSaga)
  yield debounce(300, citiesActions.setFilter.type, fetchCitiesOnFilterChangeSaga)
  yield takeLatest(citiesActions.fetchCityDetail.type, fetchCityDetailSaga)
  yield takeLatest(citiesActions.fetchCityStats.type, fetchCityStatsSaga)
  yield takeLatest(citiesActions.fetchAvailableRegions.type, fetchAvailableRegionsSaga)
  yield takeLatest(citiesActions.fetchAvailableCountries.type, fetchAvailableCountriesSaga)
  yield takeLatest(citiesActions.createCity.type, createCitySaga)
  yield takeLatest(citiesActions.updateCity.type, updateCitySaga)
  yield takeLatest(citiesActions.deleteCity.type, deleteCitySaga)
}

// ===========================================
// Root Saga
// ===========================================

export function* citiesSaga() {
  yield all([fork(watchCities)])
}
